// Single source of truth for Case Status + Case Stage + Case Priority.
//
// Manually-added cases are physically stored in one of two Firestore
// collections — "pending" or "disposed" — and many screens recompute that
// bucket from the case's `status` value to decide where to read/write and
// which tab a case shows under. Before this module every screen hard-coded
// its own list (`status === "pending" ? ... : ...`), which silently misfiled
// any status that wasn't the literal string "pending"/"disposed".
//
// Everything (AddCase, EditCaseDetails, AllCases, PendingCases, DisposedCases,
// CaseDetails, ExpensesTab, AllowAccessDetails, metricEngine) imports from here
// so the dropdown options, the collection a case lands in, and the screens that
// read it can never drift apart.

// ---- Case Status -----------------------------------------------------------

// bucket: "pending" (active / open) | "disposed" (closed / terminal).
// Only the clearly-terminal outcomes are "disposed"; on-hold or post-judgment
// follow-ups (Stayed / Restored / Appeal Filed / Execution Pending) stay
// "pending" because work is still ongoing on the matter.
export const CASE_STATUSES = [
  { value: "Pending", bucket: "pending" },
  { value: "Newly Filed", bucket: "pending" },
  { value: "Under Scrutiny", bucket: "pending" },
  { value: "Registered", bucket: "pending" },
  { value: "Notice Issued", bucket: "pending" },
  { value: "Notice Served", bucket: "pending" },
  { value: "Summons Issued", bucket: "pending" },
  { value: "Summons Served", bucket: "pending" },
  { value: "Reply Awaited", bucket: "pending" },
  { value: "Counter Affidavit Pending", bucket: "pending" },
  { value: "Counter Affidavit Filed", bucket: "pending" },
  { value: "Rejoinder Pending", bucket: "pending" },
  { value: "Rejoinder Filed", bucket: "pending" },
  { value: "Evidence Stage", bucket: "pending" },
  { value: "Arguments Stage", bucket: "pending" },
  { value: "Judgment Reserved", bucket: "pending" },
  { value: "Stayed", bucket: "pending" },
  { value: "Restored", bucket: "pending" },
  { value: "Appeal Filed", bucket: "pending" },
  { value: "Execution Pending", bucket: "pending" },
  { value: "Disposed", bucket: "disposed" },
  { value: "Dismissed", bucket: "disposed" },
  { value: "Allowed", bucket: "disposed" },
  { value: "Partly Allowed", bucket: "disposed" },
  { value: "Withdrawn", bucket: "disposed" },
  { value: "Settled", bucket: "disposed" },
  { value: "Transferred", bucket: "disposed" },
  { value: "Closed", bucket: "disposed" },
];

// {value,label} shape for <select> rendering and the metric-card filter.
export const CASE_STATUS_OPTIONS = CASE_STATUSES.map((s) => ({
  value: s.value,
  label: s.value,
}));

// Legacy status values written by older builds of the app. Kept only so old
// documents still resolve to the right bucket / a tidy display label; they are
// intentionally NOT offered in the dropdowns any more.
const LEGACY_STATUS_LABELS = {
  NA: "N/A",
  pending: "Pending",
  "pre Admission": "Pre Admission",
  admitted: "Admitted",
  adjourn: "Adjourn",
  disposed: "Disposed",
  dismissed: "Dismissed",
  rejected: "Rejected",
};

// Lower-cased lookup of every value (new + legacy) that belongs to the
// "disposed" bucket. Anything not in here is treated as "pending".
const DISPOSED_VALUES = new Set([
  ...CASE_STATUSES.filter((s) => s.bucket === "disposed").map((s) =>
    s.value.toLowerCase()
  ),
  "rejected", // legacy-only terminal status
]);

/**
 * Which Firestore collection ("pending" | "disposed") a case with this status
 * belongs to. Handles both the new status list and legacy values, and is
 * case-insensitive / whitespace-tolerant. Empty/unknown defaults to "pending".
 */
export const getStatusBucket = (status) =>
  DISPOSED_VALUES.has(String(status || "").trim().toLowerCase())
    ? "disposed"
    : "pending";

// Alias — reads better at write sites that pick a collection name.
export const getStatusCollection = getStatusBucket;

export const isDisposedStatus = (status) => getStatusBucket(status) === "disposed";

/** Human-readable label for any status value (new or legacy). */
export const statusLabel = (value) => {
  if (!value) return value;
  const opt = CASE_STATUSES.find(
    (o) => o.value.toLowerCase() === String(value).toLowerCase()
  );
  if (opt) return opt.value;
  return LEGACY_STATUS_LABELS[value] || value;
};

// ---- Case Priority ---------------------------------------------------------

// How urgent the case is, as chosen by the user on AddCase. Free-form metadata
// only — it does NOT affect which collection a case is stored in.
//
// This is deliberately NOT derived from the hearing date. PendingCases used to
// compute priority from hearing urgency (overdue -> High, <=7 days -> Medium,
// else Low), which meant every overdue case was forcibly "High" and the value
// could never be set by hand. "Is the hearing overdue?" is still answered by
// isOverdue() on that screen; the two ideas are now separate.
//
// Cases created before this field existed — and any written by the Flutter app
// — simply carry no `priority` and render as "Not set".
export const CASE_PRIORITIES = ["High", "Medium", "Low"];

const PRIORITY_META = {
  high: { key: "high", label: "High", dot: "bg-red-500", text: "text-red-600" },
  medium: { key: "medium", label: "Medium", dot: "bg-amber-500", text: "text-amber-600" },
  low: { key: "low", label: "Low", dot: "bg-emerald-500", text: "text-emerald-600" },
};

// Shown when a case has no `priority` stored (the default for every case that
// predates the field). key is used by the PendingCases "Not set" filter.
export const PRIORITY_UNSET = {
  key: "unset",
  label: "Not set",
  dot: "bg-slate-300",
  text: "text-slate-400",
};

/** Display metadata for a case's stored `priority`. Empty/unknown -> Not set. */
export const priorityMeta = (value) =>
  PRIORITY_META[String(value || "").trim().toLowerCase()] || PRIORITY_UNSET;

// ---- Case Stage ------------------------------------------------------------

// Procedural stage of a case (where it currently sits in the litigation
// lifecycle). Generic so it works across writ/civil/criminal matters as well
// as tribunals (NCLT, RERA, NGT, Consumer). Free-form metadata only — it does
// NOT affect which collection a case is stored in.
export const CASE_STAGES = [
  "Case Filed",
  "Scrutiny",
  "Registration",
  "Admission",
  "Notice Stage",
  "Service of Notice",
  "Pleadings",
  "Counter Affidavit",
  "Rejoinder",
  "Framing of Issues",
  "Evidence by Petitioner",
  "Evidence by Respondent",
  "Cross Examination",
  "Final Evidence",
  "Final Arguments",
  "Judgment Reserved",
  "Judgment Pronounced",
  "Order Passed",
  "Execution",
  "Appeal",
  "Review",
  "Revision",
  "Transferred",
  "Disposed",
  "Closed",
];
